/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import styled from 'styled-components';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import StyledProps from '../../components/common/theme/AppThemeInterface';
import { breakpoints } from './utils/StyledComponentsBreakpoints';

const TAB_ICON_SIZE = 24;

const TabsWrapper = styled.div`
  width: 100%;

  ul {
    padding: 0;
    margin: 0;
    list-style: none;

    a {
      text-decoration: none;
    }

    > a > div {
      color: ${(props: StyledProps) => props.theme.primaryTextColor};
      display: flex;
      cursor: pointer;
      position: relative;
      padding: 0px;

      > div {
        margin: 0;
        white-space: nowrap;

        > span {
          color: ${(props: StyledProps) => props.theme.primaryTextColor};
          font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
          font-size: 14px;
        }
      }

      button {
        width: ${({ theme }: StyledProps) => theme.appHeaderHeightSmall};
        color: ${({ theme }: StyledProps) => theme.primaryButtonTextColor};
        opacity: 0.65;
        padding: padding: calc((${({ theme }: StyledProps) => theme.appHeaderHeightSmall} - ${TAB_ICON_SIZE}px) / 2);

        @media only screen and (min-width: ${breakpoints.sm}px) {
          padding: calc((${({ theme }: StyledProps) => theme.appHeaderHeightLarge} - ${TAB_ICON_SIZE}px) / 2);
        }
      }

      &.selected {
        background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};

        button {
          color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
          opacity: 1;
        }
      }
    }
  }
`;

export interface TabsProps {
  children?: JSX.Element[];
  className?: string;
}

export interface TabProps {
  selected?: boolean;
  icon: JSX.Element;
  id: string;
  text: string;

  onClick?(): (event: React.ChangeEvent<{}>) => void;
}

const VerticalTabs: React.StatelessComponent<TabsProps> = (props: TabsProps): JSX.Element => {
  const { children, className } = props;

  return (
    <TabsWrapper className={`vertical-tabs ${className || ''}`}>
      <List>{children}</List>
    </TabsWrapper>
  );
};

VerticalTabs.defaultProps = {};

const Tab: React.StatelessComponent<TabProps> = (props: TabProps): JSX.Element => {
  const { selected, icon, onClick, id, text } = props;
  return (
    <ListItem
      button
      className={selected ? 'selected' : ''}
      key={id}
      onClick={onClick}
      data-test={`${id}-vertical-link`} 
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );
};

export { Tab };
export default VerticalTabs;
